import React from 'react';
import PropTypes from 'prop-types';

// Utils
import bemify from 'utils/bemify';
import formatPrice from 'utils/format-price';

// Hooks
import useAllergens from 'hooks/use-allergens';

// Components
import Icon from 'components/icon';
import Image from 'components/image';
import QuantitySelector from 'components/quantity-selector';
import Tooltip from 'components/tooltip';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);


const RestaurantMenuSidebarCartItem = ({
  allergensTranslations = null,
  onQuantityUpdate,
  product,
  selectedLocale
}) => {
  const allAllergens = useAllergens(allergensTranslations);

  // Computed
  const productTitle = (
    product?.translations?.[selectedLocale]?.title ||
    product?.fallbackTranslations?.title
  );

  // Methods
  const handleQuantityUpdate = (quantity) => {
    onQuantityUpdate(product.entityId, quantity);
  };

  return (
    <li
      key={product.entityId}
      className={styles.cartItem}
    >
      {!!product.imageUrl && (
        <div className={styles.cartItem__imageWrapper}>
          <Image
            alt={productTitle}
            layout="fill"
            objectFit="cover"
            sizes="120px"
            src={product.imageUrl}
          />
        </div>
      )}
      <div className={styles.cartItem__details}>
        <span className={styles.cartItem__title}>
          {productTitle}
        </span>
        {Array.isArray(product.allergens) && !!product.allergens.length && (
          <ul className={styles.cartItem__allergens}>
            {product.allergens.map((entry) => {
              const productAllergenDetails = allAllergens.find(allergen => allergen.id === entry);

              if (!productAllergenDetails) return null;

              return (
                <li
                  key={entry}
                  className={styles.cartItem__allergen}
                >
                  <Tooltip
                    text={productAllergenDetails.label}
                    trigger={(
                      <div>
                        <Icon
                          className={styles.cartItem__allergen}
                          name={productAllergenDetails.icon}
                        />
                      </div>
                    )}
                  />
                </li>
              );
            })}
          </ul>
        )}
        <footer className={styles.cartItem__footer}>
          <QuantitySelector
            min={0}
            value={product.quantity}
            onChange={handleQuantityUpdate}
          />
          <span className={styles.cartItem__price}>
            {formatPrice(product.price)}
          </span>
        </footer>
      </div>
    </li>
  );
};

RestaurantMenuSidebarCartItem.propTypes = {
  allergensTranslations: PropTypes.objectOf(PropTypes.string),
  onQuantityUpdate: PropTypes.func.isRequired,
  product: PropTypes.shape({
    allergens: PropTypes.arrayOf(PropTypes.string),
    entityId: PropTypes.string.isRequired,
    fallbackTranslations: PropTypes.shape({
      title: PropTypes.string
    }),
    imageUrl: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    quantity: PropTypes.number.isRequired,
    translations: PropTypes.objectOf(PropTypes.shape({
      title: PropTypes.string
    }))
  }).isRequired,
  selectedLocale: PropTypes.string.isRequired
};

export default RestaurantMenuSidebarCartItem;
